import { z } from 'zod';
import { ActorIdSchema } from '@reflex/shared';
import { FlowNodeTypeSchema, FlowApprovalAssigneeSchema } from './nodes.js';

export const FlowRunStatusSchema = z.enum([
  'running',
  'waiting_approval',
  'pending_consent',
  'completed',
  'error',
  'cancelled',
  'denied',
]);
export type FlowRunStatus = z.infer<typeof FlowRunStatusSchema>;

/**
 * Statuses of runs that have ended and can never make progress again.
 * `denied` covers consent denials, unmappable actors, and invalidated
 * parked runs.
 */
export const TERMINAL_RUN_STATUSES = [
  'completed',
  'error',
  'cancelled',
  'denied',
] as const satisfies readonly FlowRunStatus[];
export type FlowRunTerminalStatus = (typeof TERMINAL_RUN_STATUSES)[number];

/**
 * Statuses of runs that are still going: executing, or parked awaiting a
 * human (`waiting_approval`, `pending_consent`). Every {@link FlowRunStatus}
 * is exactly one of live or terminal — derive any live/history/active
 * partition from these lists instead of hand-listing statuses at the call
 * site, so a new status can never be dropped from one copy of the split.
 */
export const LIVE_RUN_STATUSES = FlowRunStatusSchema.options.filter(
  (status): status is FlowRunLiveStatus => !isRunTerminal(status),
);
export type FlowRunLiveStatus = Exclude<FlowRunStatus, FlowRunTerminalStatus>;

export function isRunTerminal(status: string): status is FlowRunTerminalStatus {
  return (TERMINAL_RUN_STATUSES as readonly string[]).includes(status);
}

export const FlowRunNodeStatusSchema = z.enum([
  'pending',
  'running',
  'waiting_approval',
  'completed',
  'error',
  'skipped',
]);
export type FlowRunNodeStatus = z.infer<typeof FlowRunNodeStatusSchema>;

export const FlowRunSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  flowName: z.string(),
  /**
   * Published `flow_versions` snapshot this run executed, pinned at run
   * creation. Null for runs of unpublished drafts and built-in flows, which
   * execute the live definition. Version snapshots are immutable, so this id
   * answers "what actually ran" for historical runs.
   */
  flowVersionId: z.string().nullable().optional(),
  streamId: z.string().nullable().optional(),
  status: FlowRunStatusSchema,
  triggerData: z.record(z.string(), z.unknown()).nullable(),
  /**
   * Id of the trigger node this run was anchored at. Null for runs of
   * legacy graphs that predate trigger nodes (which start at the graph's
   * entry nodes instead).
   */
  triggerNodeId: z.string().nullable().optional(),
  /**
   * True when the run ended because a filter node rejected the event and
   * no real work (anything beyond trigger/filter nodes) ran. Lets run
   * lists de-noise the high-volume "event arrived but didn't match" case.
   */
  filtered: z.boolean().optional(),
  state: z.record(z.string(), z.unknown()),
  currentNodeIds: z.array(z.string()),
  userId: z.string().nullable().optional(),
  /**
   * Organization that triggered this run. Stamped at run creation time
   * by the host plugin (the engine itself is org-agnostic). Even for
   * system flows — whose definition is shared across every org — each
   * run is private to the org that started it. Always required —
   * non-org callers must use `'system' sentinel`.
   */
  organizationId: z.string(),
  createdAt: z.number(),
  completedAt: z.number().nullable().optional(),
});
export type FlowRun = z.infer<typeof FlowRunSchema>;

/**
 * Slim projection of a {@link FlowRun} carrying only the fields the runs
 * list renders. Excludes the heavy `state` JSON blob (and `triggerData`,
 * `organizationId`, and the other run-only fields) so the list endpoint
 * never reads, parses, or serializes them. `GET /flow-runs` returns this
 * shape; full-run reads (`GET /flow-runs/:id`) still return {@link FlowRun}.
 */
export const FlowRunSummarySchema = z.object({
  id: z.string(),
  flowId: z.string(),
  flowName: z.string(),
  /** Published version snapshot the run executed; see {@link FlowRunSchema}. */
  flowVersionId: z.string().nullable().optional(),
  status: FlowRunStatusSchema,
  currentNodeIds: z.array(z.string()),
  userId: z.string().nullable().optional(),
  createdAt: z.number(),
  completedAt: z.number().nullable().optional(),
});
export type FlowRunSummary = z.infer<typeof FlowRunSummarySchema>;

/**
 * One page of the cursor-paginated runs list (`GET /flow-runs`). `nextCursor`
 * is an opaque keyset cursor (createdAt + id) to pass back as `?cursor=` for
 * the following page, or `null` when the list is exhausted. Keeps each
 * response bounded so the hot list path never serializes an unbounded array.
 */
export const FlowRunSummaryPageSchema = z.object({
  runs: z.array(FlowRunSummarySchema),
  nextCursor: z.string().nullable(),
});
export type FlowRunSummaryPage = z.infer<typeof FlowRunSummaryPageSchema>;

/** Per-status run counts for one flow, used by the runs dashboards. */
export const FlowRunStatusCountsSchema = z.object({
  running: z.number(),
  waiting_approval: z.number(),
  pending_consent: z.number(),
  completed: z.number(),
  error: z.number(),
  cancelled: z.number(),
  denied: z.number(),
});
export type FlowRunStatusCounts = z.infer<typeof FlowRunStatusCountsSchema>;

/**
 * Aggregate rollup for a single flow (`GET /flow-runs/stats`). Computed from
 * cheap grouped/`DISTINCT ON` queries that never read the heavy `state` blob,
 * so the dashboards can show exact totals, active counts, and the latest run
 * per flow without pulling (and counting) the full paginated run list.
 *
 * Counts are org-wide: unlike the list, they are NOT assignee-filtered, so a
 * `waiting_approval` run awaiting someone else's approval is still counted.
 */
export const FlowRunStatsSchema = z.object({
  flowId: z.string(),
  total: z.number(),
  counts: FlowRunStatusCountsSchema,
  latest: FlowRunSummarySchema.nullable(),
});
export type FlowRunStats = z.infer<typeof FlowRunStatsSchema>;

/**
 * Bounded, owner-scoped slice of {@link FlowRunSummary}s served from
 * `GET /flow-runs/inbox`. The inbox renders two buckets — failed and
 * recently completed — for runs the caller owns (either attributed to
 * them via `userId` or owned by a flow in their user/team tiers). The
 * shape is explicit (no `state` / `triggerData`) so the inbox never
 * over-fetches and never rides the org-wide runs-list invalidation.
 */
export const FlowRunInboxSchema = z.object({
  errors: z.array(FlowRunSummarySchema),
  recentCompleted: z.array(FlowRunSummarySchema),
});
export type FlowRunInbox = z.infer<typeof FlowRunInboxSchema>;

/**
 * A reviewer's answer as submitted from a surface (route bodies, the web
 * client). Deliberately excludes `decidedBy` — the decider is stamped
 * server-side and is never client-supplied; an unknown key is stripped at
 * parse.
 */
export const FlowApprovalAnswerSchema = z
  .object({
    approved: z.boolean().optional(),
    decision: z.string().optional(),
    feedback: z.string().optional(),
  })
  .refine((value) => value.approved !== undefined || !!value.decision, {
    message: 'approval response requires approved or decision',
  });
export type FlowApprovalAnswer = z.infer<typeof FlowApprovalAnswerSchema>;

/** An interactive submission names the approval incarnation. Legacy clients may answer iteration 1. */
export const FlowApprovalSubmissionSchema = FlowApprovalAnswerSchema.extend({
  iteration: z.number().int().positive().optional(),
});
export type FlowApprovalSubmission = z.infer<typeof FlowApprovalSubmissionSchema>;

/** The persisted record: the answer plus server-stamped resolution facts. */
export const FlowApprovalResponseSchema = FlowApprovalAnswerSchema.extend({
  /**
   * Principal who answered — a member or service-account user id. Stamped
   * server-side from the authenticated caller; optional only for historical records.
   */
  decidedBy: ActorIdSchema.optional(),
});
export type FlowApprovalResponse = z.infer<typeof FlowApprovalResponseSchema>;

/** Read compatibility: retain historical attribution verbatim; never use it as authority. */
export const StoredFlowApprovalResponseSchema = FlowApprovalAnswerSchema.extend({
  decidedBy: z.string().optional(),
});

export const FlowRunNodeSchema = z.object({
  id: z.string(),
  runId: z.string(),
  nodeId: z.string(),
  nodeType: FlowNodeTypeSchema,
  nodeName: z.string(),
  agentId: z.string().nullable().optional(),
  status: FlowRunNodeStatusSchema,
  input: z.record(z.string(), z.unknown()).nullable().optional(),
  output: z.record(z.string(), z.unknown()).nullable().optional(),
  outputLabel: z.string().nullable().optional(),
  approvalResponse: StoredFlowApprovalResponseSchema.nullable().optional(),
  /**
   * Effective approval targets resolved when an approval node enters
   * `waiting_approval` — either the node's explicit `reportTo` or, when
   * empty, the run's owner (the owning flow's owner for ownerless runs).
   * Only set on approval run-nodes; used to gate the approve/reject route
   * and to filter the inbox to assignees.
   */
  assignees: z.array(FlowApprovalAssigneeSchema).nullable().optional(),
  iteration: z.number().int().default(1),
  startedAt: z.number().nullable().optional(),
  completedAt: z.number().nullable().optional(),
});
export type FlowRunNode = z.infer<typeof FlowRunNodeSchema>;
